import { useState } from "react";
import { Pause, Play, Save, Trash2, Users2 } from "lucide-react";
import { Card, CardBody } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/field";
import { Alert } from "@/components/ui/alert";
import { PacingSummary } from "@/components/pacing-summary";
import { cn } from "@/lib/cn";
import {
    useDeleteAccount,
    usePauseAccount,
    useUpdateAccount,
    type AccountSettings,
    type AccountStatus,
    type ZaloAccount,
} from "@/lib/accounts";

const STATUS_LABEL: Record<AccountStatus, { text: string; className: string }> = {
    ACTIVE: { text: "Đang hoạt động", className: "bg-success/10 text-success" },
    PAUSED: { text: "Tạm dừng", className: "bg-warning/15 text-warning" },
    NEEDS_RELOGIN: { text: "Cần quét QR lại", className: "bg-danger/10 text-danger" },
    PENDING_QR: { text: "Chờ quét QR", className: "bg-ink-muted/10 text-ink-muted" },
    DISABLED: { text: "Đã tắt", className: "bg-ink-muted/10 text-ink-muted" },
};

function toSettings(account: ZaloAccount): AccountSettings {
    const { minDelayMs, maxDelayMs, concurrency, maxPerHour, maxPerDay, activeFrom, activeTo } = account;
    return { minDelayMs, maxDelayMs, concurrency, maxPerHour, maxPerDay, activeFrom, activeTo };
}

export function AccountCard({ account }: { account: ZaloAccount }) {
    const [form, setForm] = useState<AccountSettings>(() => toSettings(account));
    const update = useUpdateAccount();
    const pause = usePauseAccount();
    const remove = useDeleteAccount();

    const status = STATUS_LABEL[account.status];
    const dirty = JSON.stringify(form) !== JSON.stringify(toSettings(account));
    const isPaused = account.status === "PAUSED";

    function set<K extends keyof AccountSettings>(key: K, value: AccountSettings[K]) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    function numberField(key: keyof AccountSettings, label: string, hint?: string) {
        return (
            <Field label={label} htmlFor={`${account.id}-${key}`} hint={hint}>
                <Input
                    id={`${account.id}-${key}`}
                    type="number"
                    value={form[key] as number}
                    onChange={(event) => set(key, Number(event.target.value) as never)}
                />
            </Field>
        );
    }

    return (
        <Card>
            <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
                <div className="flex min-w-0 items-center gap-3">
                    {account.avatarUrl ? (
                        <img src={account.avatarUrl} alt="" className="size-10 shrink-0 rounded-full" />
                    ) : (
                        <div className="size-10 shrink-0 rounded-full bg-surface-muted" />
                    )}
                    <div className="min-w-0">
                        <p className="truncate font-medium text-ink">{account.displayName}</p>
                        <p className="flex items-center gap-1.5 text-xs text-ink-muted">
                            <Users2 className="size-3.5" />
                            {account.groupCount} nhóm
                            {account.phone && <span>· {account.phone}</span>}
                        </p>
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                    <span className={cn("rounded-full px-2.5 py-1 text-xs font-medium", status.className)}>
                        {status.text}
                    </span>
                    <Button
                        variant="secondary"
                        size="sm"
                        loading={pause.isPending}
                        onClick={() => pause.mutate({ id: account.id, paused: !isPaused })}
                        title={isPaused ? "Tiếp tục" : "Tạm dừng"}
                    >
                        {isPaused ? <Play className="size-4" /> : <Pause className="size-4" />}
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        loading={remove.isPending}
                        onClick={() => {
                            if (confirm(`Xoá tài khoản ${account.displayName}? Lịch sử gửi của tài khoản này cũng bị xoá.`)) {
                                remove.mutate({ id: account.id });
                            }
                        }}
                        title="Xoá tài khoản"
                    >
                        <Trash2 className="size-4 text-danger" />
                    </Button>
                </div>
            </div>

            <CardBody className="space-y-4">
                {account.lastError && <Alert tone="danger">{account.lastError}</Alert>}

                <PacingSummary
                    settings={form}
                    groupCount={account.groupCount}
                    onApplyPreset={(next) => setForm((prev) => ({ ...prev, ...next }))}
                />

                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    {numberField("minDelayMs", "Nghỉ tối thiểu (ms)")}
                    {numberField("maxDelayMs", "Nghỉ tối đa (ms)")}
                    {numberField("maxPerHour", "Tối đa / giờ")}
                    {numberField("maxPerDay", "Tối đa / ngày")}
                    {numberField("concurrency", "Gửi song song", "Để 1 là gửi tuần tự — an toàn nhất")}
                    <Field label="Bắt đầu gửi" htmlFor={`${account.id}-from`}>
                        <Input
                            id={`${account.id}-from`}
                            value={form.activeFrom}
                            onChange={(event) => set("activeFrom", event.target.value)}
                        />
                    </Field>
                    <Field label="Ngừng gửi" htmlFor={`${account.id}-to`}>
                        <Input
                            id={`${account.id}-to`}
                            value={form.activeTo}
                            onChange={(event) => set("activeTo", event.target.value)}
                        />
                    </Field>
                </div>

                {dirty && (
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setForm(toSettings(account))}>
                            Hoàn tác
                        </Button>
                        <Button
                            size="sm"
                            loading={update.isPending}
                            onClick={() => update.mutate({ id: account.id, settings: form })}
                        >
                            <Save className="size-4" />
                            Lưu cấu hình
                        </Button>
                    </div>
                )}
            </CardBody>
        </Card>
    );
}
